import { useReactiveVar } from '@apollo/client/react';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Alert, Box, Button, Grid, Snackbar, Stack, Typography } from '@mui/material';
import React, { useContext, useState } from 'react';

import ReportDetails from '../components/ReportComponents';

import HeaderSection from '@/components/ReportComponents/HeaderSection';
import ReportDetailsV2 from '@/components/ReportComponentsV2';
import HeaderSectionV2 from '@/components/ReportComponentsV2/HeaderSectionV2';
import { ReportContext } from '@/Contexts/ReportContext';
import { arrayLength } from '@/utils/helperFunctions';
import { displayNotification } from '@/utils/notifications';

const ReportPageV2: React.FC = () => {
  const notification = useReactiveVar(displayNotification);
  const { projects } = useContext(ReportContext);
  const [useClassicView, setUseClassicView] = useState(false);

  if (useClassicView) {
    return (
      <Grid container>
        <Box
          sx={{
            width: '100%',
            px: 2,
            py: 0.5,
            backgroundColor: '#0F172A',
            color: '#F8FAFC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Viewing Classic Report UI (V1)
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={() => setUseClassicView(false)}
            sx={{
              height: 24,
              fontSize: '0.7rem',
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: '#2563EB',
            }}
          >
            Switch to New Corporate UI (V2)
          </Button>
        </Box>
        <HeaderSection />
        {arrayLength(projects) > 0 ? (
          <ReportDetails />
        ) : (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            style={{ height: 'calc(100vh - 120px)' }}
            size={12}
          >
            <AssignmentIcon fontSize="large" color="action" />
            <Typography variant="h3" color="grey.500">
              No projects available
            </Typography>
          </Grid>
        )}
        <Snackbar
          open={!!notification.text}
          autoHideDuration={6000}
          onClose={() => displayNotification({ type: '', text: '' })}
        >
          <Alert
            onClose={() => displayNotification({ type: '', text: '' })}
            severity={notification.type === 'success' ? 'success' : 'error'}
            sx={{ width: '100%' }}
          >
            {notification.text}
          </Alert>
        </Snackbar>
      </Grid>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        '& *, & *::before, & *::after': {
          boxSizing: 'border-box',
        },
        minHeight: 'calc(100vh - 64px)',
        backgroundColor: '#F8FAFC',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <HeaderSectionV2 onToggleClassicView={() => setUseClassicView(true)} />
      {arrayLength(projects) > 0 ? (
        <ReportDetailsV2 />
      ) : (
        <Stack
          justifyContent="center"
          alignItems="center"
          spacing={1.5}
          sx={{ height: 'calc(100vh - 140px)', width: '100%' }}
        >
          <AssignmentIcon sx={{ fontSize: 48, color: '#94A3B8' }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#334155' }}>
            No Projects Available
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748B' }}>
            You do not currently have any active projects assigned for daily reporting.
          </Typography>
        </Stack>
      )}
      <Snackbar
        open={!!notification.text}
        autoHideDuration={6000}
        onClose={() => displayNotification({ type: '', text: '' })}
      >
        <Alert
          onClose={() => displayNotification({ type: '', text: '' })}
          severity={notification.type === 'success' ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {notification.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportPageV2;
